/* global debounce */

/* fds-search-match:start
 * Shared matcher for the predictive search dropdown (Signs by FDS).
 * A product passes when EVERY typed word matches at least one of: title, product type,
 * tags (internal "exclude-*" / "tier-*" tags ignored), vendor (the store's own vendor name is
 * ignored so "signs" doesn't match every product), or the option values of the variants
 * Shopify matched (Color etc.). Handles plurals, hyphens (t-shirt / tshirt / tee), a partial
 * last word while typing, numbers inside sizes ("5panel", "12oz"), and a small synonym map.
 */
// `var` + window guard: safe if the theme ever loads this asset twice.
var FDS_SEARCH = window.FDS_SEARCH || (() => {
  const STOP = ['a', 'an', 'and', 'the', 'of', 'for', 'in', 'with', 'to', 'by', 'on'];
  const IGNORE_VENDORS = ['signs by fds'];
  const IGNORE_TAG = /^(exclude|tier)-/i;

  // Phrase rewrites, applied to the query and to product text before splitting into words.
  const PHRASES = [
    [/\bt[\s-]?shirts?\b/g, 'tshirt'],
    [/\bzip[\s-]?ups?\b/g, 'zip'],
    [/\bcrew[\s-]?necks?\b/g, 'crewneck'],
    [/\btank[\s-]?tops?\b/g, 'tank'],
    [/\btable[\s-]?(covers?|cloths?|runners?)\b/g, 'tablecloth'],
    [/\btablecloths?\b/g, 'tablecloth'],
    [/\broyal[\s-]?blue\b/g, 'royal'],
    [/\bforest[\s-]?green\b/g, 'forest'],
    [/\bgr[ae]y\b/g, 'grey'],
    [/\bhtr\b/g, 'heather'],
    [/\bswe?a?tshi?rts?\b/g, 'sweatshirt'],
    [/\bcrw\b/g, 'crew'],
    [/\bhooded\b/g, 'hoodie'],
    [/\ba[\s-]frames?\b/g, 'aframe'],
  ];

  // Directed synonyms: typed word -> words that also satisfy it.
  const SYN = {
    tshirt: ['tshirt', 'tee', 'shirt'],
    tee: ['tee', 'tshirt', 'shirt'],
    shirt: ['shirt', 'tee', 'tshirt'],
    hoodie: ['hoodie', 'sweatshirt'],
    sweatshirt: ['sweatshirt', 'hoodie', 'crewneck', 'crew'],
    sweater: ['sweater', 'sweatshirt', 'hoodie'],
    crewneck: ['crewneck', 'crew'],
    crew: ['crew', 'crewneck'],
    hat: ['hat', 'cap', 'snapback', 'trucker', 'boonie'],
    cap: ['cap', 'hat', 'snapback', 'trucker'],
    card: ['card'],
    sign: ['sign', 'signage'],
    magnet: ['magnet', 'magnetic'],
    car: ['car', 'vehicle', 'auto', 'truck'],
    vehicle: ['vehicle', 'car', 'auto', 'truck'],
    foam: ['foam', 'gator'],
    tent: ['tent', 'canopy'],
    canopy: ['canopy', 'tent'],
    decal: ['decal', 'sticker'],
    label: ['label', 'sticker'],
    flyer: ['flyer', 'postcard'],
    womens: ['womens', 'women', 'ladies'],
    mens: ['mens', 'men'],
  };

  const norm = (s) => {
    // Drop @handles (e.g. "DM us on Instagram @signs_by_fds" inside quantity option values).
    let t = String(s == null ? '' : s).toLowerCase().replace(/@\S+/g, ' ').replace(/[’'`]/g, '');
    for (const [re, rep] of PHRASES) t = t.replace(re, rep);
    return t.replace(/[^a-z0-9]+/g, ' ').trim();
  };
  const same = (a, t) => a === t || a === `${t}s` || t === `${a}s` || a === `${t}es` || t === `${a}es`
    || a.replace(/ies$/, 'y') === t.replace(/ies$/, 'y');
  const alts = (w) => {
    const key = Object.keys(SYN).find((k) => same(w, k));
    return key ? SYN[key] : [w];
  };
  const words = (q) => norm(q).split(' ').filter((w) => w && !STOP.includes(w));

  // fields: { title, type, tags[], vendor, variants: [{ title, options[] }] }
  const fieldText = (p) => ({
    main: [p.title, p.type, ...(p.tags || []).filter((t) => !IGNORE_TAG.test(t)),
      IGNORE_VENDORS.includes(String(p.vendor || '').toLowerCase()) ? '' : p.vendor].join(' '),
    variants: (p.variants || []).map((v) => [v.title, ...(v.options || [])]
      .filter((x) => x && !/^default title$/i.test(x)).join(' ')),
  });

  const wordHits = (w, isLast, text) => {
    const n = norm(text);
    const tokens = n.split(' ');
    const compact = n.replace(/ /g, '');
    return alts(w).some((a) => tokens.some((t) => same(a, t) || (isLast && a.length >= 2 && t.startsWith(a)))
      || (/\d/.test(a) && compact.includes(a)));
  };

  /**
   * @returns {null | {variantIndex: number|null, viaVariant: string[]}} null = drop the product.
   */
  const match = (query, product) => {
    const ws = words(query);
    if (!ws.length) return { variantIndex: null, viaVariant: [] };
    const f = fieldText(product);
    const viaVariant = [];
    for (let i = 0; i < ws.length; i += 1) {
      const isLast = i === ws.length - 1;
      if (wordHits(ws[i], isLast, f.main)) continue;
      if (f.variants.some((vt) => wordHits(ws[i], isLast, vt))) { viaVariant.push(ws[i]); continue; }
      return null;
    }
    let variantIndex = null;
    if (viaVariant.length) {
      const idx = f.variants.findIndex((vt) => viaVariant.every((w, i) => wordHits(w, i === viaVariant.length - 1, vt)));
      variantIndex = idx >= 0 ? idx : f.variants.findIndex((vt) => wordHits(viaVariant[0], true, vt));
    }
    return { variantIndex, viaVariant };
  };

  /**
   * Strips characters Shopify's search treats as query syntax, so pasting a product title works:
   * " -DTF" would mean "NOT DTF", "( )" groups, ":" targets a field, "*" is a wildcard, quotes force
   * a phrase and upper-case AND/OR/NOT are operators. Hyphens inside words (Zip-Up) are kept.
   */
  const cleanQuery = (q) => String(q)
    .replace(/[()[\]{}":*\\]/g, ' ')
    .replace(/(^|\s)[-+!]+(?=\S)/g, '$1')
    .replace(/\b(AND|OR|NOT)\b/g, (m) => m.toLowerCase())
    .replace(/\s+/g, ' ')
    .trim();

  return { norm, words, match, cleanQuery };
})();
window.FDS_SEARCH = FDS_SEARCH;
/* fds-search-match:end */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (!this.getQuery().length || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')) {
        evt.preventDefault();
        return;
      }
      // Send the /search results page the same cleaned query the dropdown uses (see cleanQuery).
      const cleaned = FDS_SEARCH.cleanQuery(this.getQuery());
      if (cleaned && cleaned !== this.getQuery()) this.input.value = cleaned;
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector('[role="tabpanel"]:not([hidden]) [aria-selected="true"]');
      const allResults = this.querySelectorAll('[role="tabpanel"]:not([hidden]) .predictive-search__item');
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * Fetches the rendered dropdown (HTML) and, in parallel, the same predictive search as JSON so
     * the filter can see each product's type, tags, vendor and the variants Shopify matched (Color).
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      const queryKey = searchTerm.trim().toLowerCase().replace(/\s+/g, '-');
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      // How many results to show per tab, and how many to ask for (the filter can drop some).
      const showLimit = Number(theme.settings.pSearchLimit) || 5;
      const fetchLimit = Math.min(10, showLimit * 2);

      let searchFields = 'title,product_type,variants.title,tag,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';

      let searchParams = `q=${encodeURIComponent(FDS_SEARCH.cleanQuery(searchTerm) || searchTerm)}`;
      searchParams += `&${encodeURIComponent('resources[limit]')}=${fetchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${theme.settings.pSearchLimitScope}`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;

      const htmlUrl = `${theme.routes.predictiveSearch}?${searchParams}`
        + `&${encodeURIComponent('resources[type]')}=product,collection,page&section_id=predictive-search`;
      const jsonUrl = `${theme.routes.predictiveSearch}.json?${searchParams}`
        + `&${encodeURIComponent('resources[type]')}=product`;

      try {
        const [response, jsonResponse] = await Promise.all([
          fetch(htmlUrl),
          fetch(jsonUrl).catch(() => null),
        ]);
        if (!response.ok) throw new Error(response.status);

        let products = [];
        if (jsonResponse && jsonResponse.ok) {
          try {
            const data = await jsonResponse.json();
            products = (data.resources && data.resources.results && data.resources.results.products) || [];
          } catch (e) {
            products = [];
          }
        }

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = await this.filterResults(
          resultsEl.innerHTML.replace(/psearch/g, this.input.id), searchTerm, products, showLimit,
        );

        // Ignore a slow response if the shopper has already typed something else.
        if (this.getQuery() !== searchTerm) return;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Returns full product data ({ title, type, tags, vendor, variants }) for a handle from
     * /products/{handle}.js, cached for the page view. Used only when a result's title alone
     * doesn't match and the predictive JSON didn't include that product (Shopify's HTML and JSON
     * responses are not always the same result set).
     * @param {string} handle - Product handle.
     * @returns {Promise<object|null>}
     */
    fetchProductData(handle) {
      this.productCache = this.productCache || {};
      if (!this.productCache[handle]) {
        this.productCache[handle] = fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}products/${encodeURIComponent(handle)}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .then((p) => (p ? {
            title: p.title,
            type: p.type,
            tags: p.tags,
            vendor: p.vendor,
            variants: (p.variants || []).map((v) => ({
              title: v.title,
              options: v.options,
              id: v.id,
              image: (v.featured_image && v.featured_image.src) || null,
            })),
          } : null))
          .catch(() => null);
      }
      return this.productCache[handle];
    }

    /**
     * Keeps product results where every typed word matches the title, product type, tags, vendor
     * or a variant's option values (see FDS_SEARCH above). When a word only matched a variant
     * (e.g. a Color), the result links to that variant (?variant=ID) and shows its image.
     * Product data comes from the predictive JSON; if a product is missing there and its title
     * alone doesn't match, /products/{handle}.js is used. Each tab is then trimmed to the theme's
     * result limit, empty tabs are removed and the no-results state is shown if nothing is left.
     * Any error returns the unfiltered markup.
     * @param {string} markup - Results markup.
     * @param {string} searchTerm - Search query.
     * @param {Array} products - Products from /search/suggest.json (same query).
     * @param {number} showLimit - Max results to show per tab.
     * @returns {Promise<string>}
     */
    async filterResults(markup, searchTerm, products, showLimit) {
      try {
        if (!FDS_SEARCH.words(searchTerm).length) return markup;

        const byHandle = {};
        (products || []).forEach((p) => {
          byHandle[p.handle] = {
            title: p.title,
            type: p.type,
            tags: p.tags,
            vendor: p.vendor,
            variants: (p.variants || []).map((v) => ({
              title: v.title,
              url: v.url,
              id: v.id,
              image: (v.featured_image && v.featured_image.url) || v.image || null,
            })),
          };
        });
        const handleOf = (href) => {
          const m = /\/products\/([^?#/]+)/.exec(href || '');
          return m ? decodeURIComponent(m[1]) : null;
        };
        const sizedImg = (url, width) => `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
        const withVariant = (href, id) => {
          const url = new URL(href, window.location.origin);
          url.searchParams.set('variant', id);
          return `${url.pathname}${url.search}${url.hash}`;
        };

        const tmpl = document.createElement('template');
        tmpl.innerHTML = markup;
        const root = tmpl.content;
        const productsPanel = root.querySelector('[role="tabpanel"][id$="-products"]');
        if (!productsPanel) return markup;

        const items = [...productsPanel.querySelectorAll('.predictive-search__item:not(.js-submit)')];
        await Promise.all(items.map(async (item) => {
          const link = item.querySelector('a.js-search-link');
          const titleEl = item.querySelector('.product-card-mini__title');
          if (!link || !titleEl) return;
          const handle = handleOf(link.getAttribute('href'));
          let data = byHandle[handle];
          let result = data ? FDS_SEARCH.match(searchTerm, data) : null;
          if (!data) {
            result = FDS_SEARCH.match(searchTerm, { title: titleEl.textContent });
            if (!result) {
              data = handle ? await this.fetchProductData(handle) : null;
              if (data) result = FDS_SEARCH.match(searchTerm, data);
            }
          }
          if (!result) {
            item.remove();
            return;
          }
          const variant = data && result.variantIndex !== null ? data.variants[result.variantIndex] : null;
          if (variant && variant.id) {
            link.setAttribute('href', variant.url || withVariant(link.getAttribute('href'), variant.id));
            const img = item.querySelector('img');
            if (img && variant.image) {
              const w = Number(img.getAttribute('width')) || 112;
              img.setAttribute('src', sizedImg(variant.image, w));
              img.setAttribute('srcset', `${sizedImg(variant.image, w)} ${w}w`);
            }
          }
        }));

        // Trim every tab to the configured limit (we asked Shopify for extra).
        root.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
          [...panel.querySelectorAll('.predictive-search__item:not(.js-submit)')]
            .slice(showLimit || 5).forEach((el) => el.remove());
        });

        root.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
          if (panel.querySelector('.predictive-search__item:not(.js-submit)')) return;
          const tab = root.querySelector(`[role="tab"][aria-controls="${panel.id}"]`);
          if (tab) tab.remove();
          panel.remove();
        });

        const tabs = root.querySelectorAll('[role="tab"]');
        if (!root.querySelector('[role="tabpanel"]')) {
          const resultsWrap = root.querySelector('.predictive-search__results');
          if (!resultsWrap) return markup;
          const noResults = document.createElement('div');
          noResults.className = 'predictive-search__no-results';
          noResults.textContent = `No results found for "${searchTerm}".`;
          resultsWrap.replaceChildren(noResults);
        } else if (tabs.length && !root.querySelector('[role="tab"][aria-selected="true"]')) {
          tabs.forEach((tab, i) => {
            tab.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
            const panel = root.querySelector(`[id="${tab.getAttribute('aria-controls')}"]`);
            if (panel) panel.hidden = i !== 0;
          });
        }

        return tmpl.innerHTML;
      } catch (error) {
        return markup;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
